import React, { useMemo, useRef, useState, useEffect } from 'react';
import { Stage, Layer, Rect, Group, Text, Shape } from 'react-konva';

export default function KonvaBottles({
  boxes,
  stepsPerBox,
  unitLabel,
  measureIncrement,
  measureMaxVal,
  actionable,
  onInc,
  onDec,
}) {
  const wrapRef = useRef(null);
  const [size, setSize] = useState({ w: 720, h: 300 });

  // Layout constants (taller than boxes for a bottle silhouette)
  const PADDING = 8;
  const GAP = 16;
  const BOTTLE_W = 120;
  const BOTTLE_H = 180;

  // Responsive
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const cr = entries[0].contentRect;
      const width = Math.max(320, cr.width);
      const cols = Math.max(1, Math.floor((width - PADDING * 2 + GAP) / (BOTTLE_W + GAP)));
      const rows = Math.ceil((boxes?.length || 0) / cols) || 1;
      const height = rows * (BOTTLE_H + GAP) - GAP + PADDING * 2;
      setSize({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [boxes?.length]);

  const cols = useMemo(() => {
    return Math.max(1, Math.floor((size.w - PADDING * 2 + GAP) / (BOTTLE_W + GAP)));
  }, [size.w]);

  const getXY = (idx) => {
    const row = Math.floor(idx / cols);
    const col = idx % cols;
    const x = PADDING + col * (BOTTLE_W + GAP);
    const y = PADDING + row * (BOTTLE_H + GAP);
    return { x, y };
  };

  const formatAmount = (steps) => {
    const inc = measureIncrement > 0 ? measureIncrement : 1;
    const val = steps * inc;
    return `${val.toFixed(0)}${unitLabel}`;
  };

  const handleEnter = (e) => {
    if (!actionable) return;
    e.target.getStage().container().style.cursor = 'pointer';
  };
  const handleLeave = (e) => {
    e.target.getStage().container().style.cursor = 'default';
  };

  // Draw a laundry-style bottle outline path at (0,0) sized to W x H
  const drawBottlePath = (ctx, W, H) => {
    const neckW = W * 0.28;
    const neckH = H * 0.18;
    const shoulderH = H * 0.15;
    const bodyH = H - neckH - shoulderH - 4;
    const shoulderCurve = W * 0.2;
    const bodyW = W * 0.78;
    const bodyX = (W - bodyW) / 2;
    const bottomRadius = 10;

    // Cap/neck
    ctx.beginPath();
    // top cap
    ctx.moveTo((W - neckW) / 2, 0);
    ctx.lineTo((W + neckW) / 2, 0);
    ctx.lineTo((W + neckW) / 2, neckH * 0.45);
    ctx.lineTo((W - neckW) / 2, neckH * 0.45);
    ctx.closePath();

    // Neck rectangle
    ctx.beginPath();
    ctx.rect((W - neckW) / 2, neckH * 0.45, neckW, neckH * 0.55);
    ctx.closePath();

    // Bottle outline with shoulders & body
    ctx.beginPath();
    const y0 = neckH;
    // start at top-left shoulder
    ctx.moveTo(bodyX, y0 + shoulderH * 0.1);
    ctx.quadraticCurveTo(bodyX - shoulderCurve, y0 + shoulderH * 0.5, bodyX, y0 + shoulderH);
    // left body wall
    ctx.lineTo(bodyX, y0 + shoulderH + bodyH - bottomRadius);
    // bottom left curve
    ctx.quadraticCurveTo(bodyX, y0 + shoulderH + bodyH, bodyX + bottomRadius, y0 + shoulderH + bodyH);
    // bottom line
    ctx.lineTo(bodyX + bodyW - bottomRadius, y0 + shoulderH + bodyH);
    // bottom right curve
    ctx.quadraticCurveTo(bodyX + bodyW, y0 + shoulderH + bodyH, bodyX + bodyW, y0 + shoulderH + bodyH - bottomRadius);
    // right body wall
    ctx.lineTo(bodyX + bodyW, y0 + shoulderH);
    // right shoulder curve
    ctx.quadraticCurveTo(bodyX + bodyW + shoulderCurve, y0 + shoulderH * 0.5, bodyX + bodyW, y0 + shoulderH * 0.1);
    // top bridge back to left
    ctx.closePath();
  };

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      <Stage width={size.w} height={size.h}>
        <Layer>
          {(boxes || []).map((steps, idx) => {
            const { x, y } = getXY(idx);
            const pct = stepsPerBox ? steps / stepsPerBox : 0;
            const fillH = Math.max(0, Math.min(BOTTLE_H, Math.round(BOTTLE_H * pct)));

            return (
              <Group
                key={idx}
                x={x}
                y={y}
                listening={actionable}
                onClick={actionable ? () => onInc(idx) : undefined}
                onTap={actionable ? () => onInc(idx) : undefined}
                onMouseEnter={handleEnter}
                onMouseLeave={handleLeave}
              >
                {/* Clip group for interior fill */}
                <Group
                  clipFunc={(ctx) => drawBottlePath(ctx, BOTTLE_W, BOTTLE_H)}
                >
                  {/* vertical fill from bottom */}
                  <Rect
                    x={0}
                    y={BOTTLE_H - fillH}
                    width={BOTTLE_W}
                    height={fillH}
                    fillLinearGradientStartPoint={{ x: 0, y: BOTTLE_H - fillH }}
                    fillLinearGradientEndPoint={{ x: 0, y: BOTTLE_H }}
                    fillLinearGradientColorStops={[0, '#0d6efd', 1, '#084298']}
                  />
                </Group>

                {/* Outline */}
                <Shape
                  sceneFunc={(ctx, shape) => {
                    drawBottlePath(ctx, BOTTLE_W, BOTTLE_H);
                    ctx.strokeShape(shape);
                  }}
                  stroke="#444"
                  strokeWidth={2}
                  fill="#f8f9fa"
                />

                {/* Label */}
                <Text
                  text={`${formatAmount(steps)} / ${measureMaxVal}${unitLabel}`}
                  x={0}
                  y={BOTTLE_H / 2 - 9}
                  width={BOTTLE_W}
                  align="center"
                  fontSize={14}
                  fill="#0d6efd"
                  listening={false}
                />
              </Group>
            );
          })}
        </Layer>
      </Stage>
    </div>
  );
}